import React, { useEffect, useRef, useState } from 'react'
import Cropper, { Area, Point } from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'
import heic2any from 'heic2any'
import { Button, IconButton, Input, Stack } from '@mui/material'
import { Box } from '@mui/system'
import { J2Delete, J2UploadToCloud } from '@e3dc-react/icons'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'

const DEFAULT_ASPECT_RATIO = 16 / 9
const DEFAULT_MAX_FILE_SIZE_BYTES = 15 * 1024 * 1024 // 15 MB
const DEFAULT_MIN_ZOOM = 1
const DEFAULT_MAX_ZOOM = 3
const ACCEPTED_IMAGE_TYPES = new Set(['image/jpg', 'image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'])
const ACCEPTED_IMAGE_INPUT_VALUE = '.jpg,.jpeg,.png,.heic,.heif,.webp,image/jpeg,image/png,image/heic,image/heif,image/webp'
const CROPPED_IMAGE_MIME_TYPE = 'image/webp'
const CROPPED_IMAGE_QUALITY = 0.92

const useStyles = makeStyles()(theme => ({
  root: {
    width: '100%',
  },
  previewContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    overflow: 'hidden',
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.grey.grey05}`,
  },
  placeholderImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    padding: theme.spacing(1),
  },
  actionIcon: {
    color: theme.palette.grey.grey00,
  },
  deleteButton: {
    '&.Mui-disabled .MuiSvgIcon-root, &.Mui-disabled svg': {
      color: theme.palette.shades.shade02,
    },
  },
  hiddenInput: {
    display: 'none',
  },
  downloadButton: {
    minWidth: 0,
  },
}))

const formatMaxFileSize = (maxFileSizeBytes: number): string => `${Math.round(maxFileSizeBytes / (1024 * 1024))} MB`

const isHeicOrHeifFile = (file: File): boolean => {
  const mimeType = file.type.toLowerCase()
  const fileName = file.name.toLowerCase()

  return mimeType === 'image/heic' || mimeType === 'image/heif' || fileName.endsWith('.heic') || fileName.endsWith('.heif')
}

const toJpegFileName = (originalName: string): string => {
  const baseName = originalName.replace(/\.[^/.]+$/, '')
  return `${baseName}.jpg`
}

const convertHeicToJpeg = async (file: File): Promise<File> => {
  const conversionResult = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92,
  })

  const convertedBlob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult

  if (!convertedBlob) {
    throw new Error('HEIC conversion failed')
  }

  return new File([convertedBlob], toJpegFileName(file.name), { type: 'image/jpeg' })
}

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Failed to load image for cropping'))
    image.src = src
  })

const cropImageToWebp = async (imageSrc: string, pixelCrop: Area): Promise<Blob> => {
  const image = await loadImage(imageSrc)

  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.floor(pixelCrop.width))
  canvas.height = Math.max(1, Math.floor(pixelCrop.height))

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('Could not initialize canvas context for image cropping')
  }

  context.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height)

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Canvas crop conversion failed'))
          return
        }
        if (blob.type === CROPPED_IMAGE_MIME_TYPE) {
          resolve(blob)
          return
        }
        // Safari/iOS: WebP encoding not supported, fall back to JPEG
        canvas.toBlob(
          jpegBlob => {
            if (!jpegBlob) {
              reject(new Error('Canvas crop conversion failed'))
              return
            }
            resolve(jpegBlob)
          },
          'image/jpeg',
          CROPPED_IMAGE_QUALITY
        )
      },
      CROPPED_IMAGE_MIME_TYPE,
      CROPPED_IMAGE_QUALITY
    )
  })
}

const toCroppedFileName = (mimeType: string): string => {
  const ext = mimeType === 'image/jpeg' ? 'jpg' : 'webp'
  return `${Date.now()}.${ext}`
}

export interface ImageUploadValue {
  file: File | null
  previewUrl: string
  crop: Point
  zoom: number
  croppedAreaPixels: Area | null
  croppedImageFormData: File | null
}

export interface Props {
  placeholderImage: string
  isTesting?: boolean
  initialImageUrl?: string | null
  canDelete?: boolean
  onChange?: (value: ImageUploadValue | null) => void
}

/**
 * ImageUpload provides a 16:9 image editor with upload, crop, and zoom support.
 * It accepts JPG/PNG/HEIC/HEIF files, converts HEIC/HEIF to JPEG for preview,
 * and emits the cropped result as WEBP as File via onChange.
 *
 * The Download WEBP button is intended for Storybook/testing usage and is shown
 * only when isTesting is enabled.
 *
 * Docs: {@link https://valentinh.github.io/react-easy-crop/docs/examples/basic}
 */
const ImageUpload: React.FC<Props> = ({ initialImageUrl = null, onChange, placeholderImage, isTesting = false, canDelete = true }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const previewUrlRef = useRef<string | null>(null)
  const hasUserInteractedRef = useRef(false)
  const hasExplicitlyRemovedRef = useRef(false)
  const { enqueueErrorStack } = useSnackbar()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(initialImageUrl)
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState<number>(DEFAULT_MIN_ZOOM)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [croppedImageFormData, setCroppedImageFormData] = useState<File | null>(null)

  const markUserInteracted = (): void => {
    hasUserInteractedRef.current = true
  }

  useEffect(() => {
    if (!selectedFile && !hasExplicitlyRemovedRef.current) {
      setImageUrl(initialImageUrl)
      hasUserInteractedRef.current = false
    }
  }, [initialImageUrl, selectedFile])

  useEffect(() => {
    if (!onChange) return

    if (!imageUrl) {
      onChange(null)
      return
    }

    if (initialImageUrl && !hasUserInteractedRef.current) {
      return
    }

    onChange({
      file: selectedFile,
      previewUrl: imageUrl,
      crop,
      zoom,
      croppedAreaPixels,
      croppedImageFormData,
    })
  }, [crop, croppedAreaPixels, croppedImageFormData, imageUrl, initialImageUrl, onChange, selectedFile, zoom])

  useEffect(() => {
    let isCancelled = false

    const createCroppedImage = async (): Promise<void> => {
      if (!imageUrl || !croppedAreaPixels) {
        setCroppedImageFormData(null)
        return
      }

      if (initialImageUrl && !hasUserInteractedRef.current) {
        setCroppedImageFormData(null)
        return
      }

      try {
        const blob = await cropImageToWebp(imageUrl, croppedAreaPixels)
        if (isCancelled) return

        const file = new File([blob], toCroppedFileName(blob.type), { type: blob.type })
        setCroppedImageFormData(file)
      } catch (error) {
        if (isCancelled) return

        console.error('Error creating cropped image:', error)
        setCroppedImageFormData(null)
      }
    }

    createCroppedImage()

    return () => {
      isCancelled = true
    }
  }, [croppedAreaPixels, imageUrl, initialImageUrl])

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current)
      }
    }
  }, [])

  const resetEditorState = (): void => {
    setCrop({ x: 0, y: 0 })
    setZoom(DEFAULT_MIN_ZOOM)
    setCroppedAreaPixels(null)
    setCroppedImageFormData(null)
  }

  const handleRemoveImage = (): void => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }

    hasExplicitlyRemovedRef.current = true
    setSelectedFile(null)
    setImageUrl(null)
    hasUserInteractedRef.current = false
    resetEditorState()

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const isImageSelected = Boolean(imageUrl)

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0]

    if (!file) return

    const isHeic = isHeicOrHeifFile(file)

    if (!isHeic && !ACCEPTED_IMAGE_TYPES.has(file.type)) {
      enqueueErrorStack(t('image-upload.unsupported-image-format'))
      return
    }

    if (file.size > DEFAULT_MAX_FILE_SIZE_BYTES) {
      enqueueErrorStack(t('image-upload.image-too-large', { size: formatMaxFileSize(DEFAULT_MAX_FILE_SIZE_BYTES) }))
      return
    }

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
    }

    let fileForPreview = file

    if (isHeic) {
      try {
        fileForPreview = await convertHeicToJpeg(file)
      } catch (error) {
        console.error('Error converting HEIC/HEIF to JPEG:', error)
        enqueueErrorStack(t('image-upload.can-not-process-heic-heif'))
        return
      }
    }

    const previewUrl = URL.createObjectURL(fileForPreview)
    previewUrlRef.current = previewUrl
    hasExplicitlyRemovedRef.current = false
    markUserInteracted()

    setSelectedFile(fileForPreview)
    setImageUrl(previewUrl)
    resetEditorState()
  }

  const handleDownloadCroppedImage = (): void => {
    const croppedImage = croppedImageFormData

    if (!(croppedImage instanceof File)) return

    const downloadUrl = URL.createObjectURL(croppedImage)
    const anchor = document.createElement('a')
    anchor.href = downloadUrl
    anchor.download = croppedImage.name
    anchor.click()
    URL.revokeObjectURL(downloadUrl)
  }

  return (
    <Stack className={classes.root}>
      <Box className={classes.previewContainer}>
        {!imageUrl && <Box component="img" src={placeholderImage} alt="upload-placeholder" className={classes.placeholderImage} />}
        {imageUrl && (
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={DEFAULT_ASPECT_RATIO}
            minZoom={DEFAULT_MIN_ZOOM}
            maxZoom={DEFAULT_MAX_ZOOM}
            restrictPosition
            objectFit="cover"
            onCropChange={nextCrop => {
              if (nextCrop.x !== crop.x || nextCrop.y !== crop.y) {
                markUserInteracted()
              }

              setCrop(nextCrop)
            }}
            onZoomChange={nextZoom => {
              if (nextZoom !== zoom) {
                markUserInteracted()
              }

              setZoom(nextZoom)
            }}
            onCropComplete={(_, nextCroppedAreaPixels) => {
              if (initialImageUrl && !hasUserInteractedRef.current) {
                return
              }

              setCroppedAreaPixels(nextCroppedAreaPixels)
            }}
          />
        )}
      </Box>

      <Stack mt={2} direction="row" alignItems="center" justifyContent="space-between">
        <IconButton onClick={handleRemoveImage} disabled={!isImageSelected || !canDelete} aria-label="delete-image" className={classes.deleteButton}>
          <J2Delete className={classes.actionIcon} />
        </IconButton>

        {isTesting && (
          <Button className={classes.downloadButton} variant="text" onClick={handleDownloadCroppedImage} disabled={!croppedImageFormData}>
            Download WEBP (StoryBook only)
          </Button>
        )}

        <IconButton component="label" aria-label="upload-image">
          <J2UploadToCloud className={classes.actionIcon} />
          <Input
            inputRef={inputRef}
            type="file"
            inputProps={{ accept: ACCEPTED_IMAGE_INPUT_VALUE }}
            onChange={handleFileChange}
            className={classes.hiddenInput}
          />
        </IconButton>
      </Stack>
    </Stack>
  )
}

export default ImageUpload
